import type * as Y from 'yjs';

/**
 * NotePad's textarea ↔ `Y.Text` binding. Plain text with newlines, no markdown rendering
 * (WS-16 brief v1) — so the whole "binding" is: translate "the textarea's value changed"
 * into one minimal `Y.Text` edit, and let Yjs do the actual CRDT merging.
 *
 * Diffs by common prefix/suffix rather than replacing the whole string on every keystroke,
 * for two reasons: (1) a delete+insert of the *entire* text on every character is not
 * conflict-free — two members typing in different parts of the note would each blow away
 * the other's edit instead of merging; a scoped `delete(start, len)` + `insert(start, …)`
 * at just the changed span is. (2) it keeps remote inserts from clobbering a local cursor
 * sitting elsewhere in the document.
 */
export function applyTextDiff(ytext: Y.Text, nextValue: string): void {
  const oldValue = ytext.toString();
  if (oldValue === nextValue) return;

  const maxPrefix = Math.min(oldValue.length, nextValue.length);
  let start = 0;
  while (start < maxPrefix && oldValue[start] === nextValue[start]) start += 1;

  let oldEnd = oldValue.length;
  let newEnd = nextValue.length;
  while (oldEnd > start && newEnd > start && oldValue[oldEnd - 1] === nextValue[newEnd - 1]) {
    oldEnd -= 1;
    newEnd -= 1;
  }

  const deleteLength = oldEnd - start;
  const inserted = nextValue.slice(start, newEnd);
  if (deleteLength === 0 && inserted.length === 0) return;
  if (deleteLength > 0) ytext.delete(start, deleteLength);
  if (inserted.length > 0) ytext.insert(start, inserted);
}
